import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AppIcon from "../../../components/ui/AppIcon";
import { useChat } from "../../../context/ChatContext";
import { useAuth } from "../../../context/AuthContext";
import { createChannel } from "../../../services/channelService";

export default function CreateChannelModal({ onClose }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { usersData, refreshData } = useChat();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [isPrivate, setIsPrivate] = useState(false);
    const [step, setStep] = useState(1);
    const [isLaunching, setIsLaunching] = useState(false);
    const [inviteInput, setInviteInput] = useState("");
    const [invitedMembers, setInvitedMembers] = useState([]);

    const handleCreate = () => {
        if (!name.trim()) return;
        setStep(2);
    };

    const handleLaunch = async () => {
        if (!name.trim() || isLaunching) return;

        const getRawRole = () => {
            if (!user) return '';
            if (user.user_role && typeof user.user_role === 'object' && user.user_role.role) {
                return user.user_role.role;
            }
            if (typeof user.user_role === 'string') {
                return user.user_role;
            }
            return user.role || '';
        };

        const roleString = getRawRole().toLowerCase().trim();
        const isAdmin = roleString === 'admin' || roleString === 'superadmin';

        if (!isAdmin) {
            toast.error("Only administrators can create channels.");
            return;
        }

        setIsLaunching(true);
        const loadingToast = toast.loading("Creating and launching channel...");

        try {
            const memberIds = invitedMembers.map(m => m._id || m.id);
            const creatorId = user?._id || user?.id;

            if (creatorId && !memberIds.includes(creatorId)) {
                memberIds.push(creatorId);
            }

            const payload = {
                name,
                description,
                isPrivate,
                members: memberIds,
                active: true
            };

            const response = await createChannel(payload);

            if (response?.success) {
                const newChannelId = response.data?._id || response.data?.id;
                toast.success("Channel created successfully!", { id: loadingToast });
                refreshData().catch(err => console.error("Post-creation refresh failed:", err));

                if (newChannelId) {
                    navigate(`/channel/${newChannelId}`);
                }

                onClose();
            } else {
                throw new Error(response.data?.message || "Failed to create channel");
            }

        } catch (error) {
            console.error("Failed to launch channel:", error);
            toast.error(error.response?.data?.message || error.message || "Failed to create channel", { id: loadingToast });
        } finally {
            setIsLaunching(false);
        }
    };

    const addMember = (member) => {
        if (!invitedMembers.find(m =>
            (m._id || m.id) === (member._id || member.id)
        )) {
            setInvitedMembers((prev) => [...prev, member]);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-xl p-4 animate-fade-in">
            <div
                className="w-full max-w-3xl bg-app-surface rounded-3xl shadow-2xl overflow-hidden animate-fly-in"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-secondary/10 rounded-full blur-3xl" />
                </div>

                <div className="relative flex">
                    {step === 1 && (
                        <div className="hidden md:flex flex-col justify-center p-8 w-1/3 bg-gradient-to-br from-primary/5 via-app-bg/50 to-transparent border-r border-border-subtle">
                            <div className="relative mb-6">
                                <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-20 blur-xl" />
                                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-xl">
                                    <AppIcon name="ChatBubbleLeftRightIcon" size={32} className="text-white" />
                                </div>
                            </div>
                            <h3 className="text-lg font-bold text-text-primary tracking-tight mb-2">
                                Create Channel
                            </h3>
                            <p className="text-sm text-text-muted leading-relaxed">
                                Build a space for your team to collaborate, share updates, and work together seamlessly.
                            </p>
                            <div className="mt-8 space-y-3">
                                <div className="flex items-center gap-3 text-xs text-text-muted">
                                    <div className="w-5 h-5 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <AppIcon name="UserGroupIcon" size={12} className="text-primary" />
                                    </div>
                                    <span>Invite team members</span>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-text-muted">
                                    <div className="w-5 h-5 rounded-lg bg-primary/10 flex items-center justify-center">
                                        <AppIcon name="LockClosedIcon" size={12} className="text-primary" />
                                    </div>
                                    <span>Choose privacy settings</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex-1 flex flex-col">
                        <div className="flex items-center justify-between px-6 py-5 border-b border-border-subtle bg-app-bg/20">
                            <div>
                                <h2 className="text-xl font-bold text-text-primary tracking-tight">
                                    {step === 1 ? "Create Channel" : "Add Members"}
                                </h2>
                                <p className="text-xs text-text-muted mt-1">
                                    {step === 1
                                        ? "Set up your new collaboration space"
                                        : `Invite people to join #${name}`}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2.5 rounded-xl text-text-muted hover:text-text-primary hover:bg-black/5 transition-all"
                            >
                                <AppIcon name="XMarkIcon" size={20} />
                            </button>
                        </div>

                        <div className="flex px-6 py-2 gap-1 bg-app-bg/10">
                            {[1, 2].map((s) => (
                                <div
                                    key={s}
                                    className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                                        s <= step 
                                            ? "bg-primary shadow-[0_0_10px_rgba(94,106,210,0.4)]" 
                                            : "bg-black/10"
                                    }`}
                                />
                            ))}
                        </div>

                        <div className="px-5 py-6 sm:px-8 sm:py-8 space-y-6">
                            {step === 1 ? (
                                <>
                                    <div className="space-y-3">
                                        <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Channel Name</label>
                                        <div className="relative group">
                                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-text-muted group-focus-within:text-primary transition-colors">#</div>
                                            <input
                                                value={name}
                                                disabled={isLaunching}
                                                onChange={(e) =>
                                                    setName(
                                                        e.target.value
                                                            .toLowerCase()
                                                            .replace(/\s+/g, "-")
                                                            .replace(/[^a-z0-9-]/g, "")
                                                    )
                                                }
                                                className="w-full pl-10 pr-4 py-3.5 rounded-2xl bg-app-bg border border-border-subtle focus:border-primary/50 text-text-primary font-medium outline-none transition-all placeholder:text-text-muted/40 shadow-subtle focus:ring-4 focus:ring-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                                placeholder="project-phoenix"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
                                            Description <span className="text-text-muted font-normal opacity-60">(Optional)</span>
                                        </label>
                                        <textarea
                                            value={description}
                                            disabled={isLaunching}
                                            onChange={(e) => setDescription(e.target.value)}
                                            className="w-full px-4 py-3.5 rounded-2xl bg-app-bg border border-border-subtle focus:border-primary/50 text-text-primary font-medium resize-none outline-none transition-all shadow-subtle focus:ring-4 focus:ring-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
                                            rows={3}
                                            placeholder="What is this channel about?"
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-5">
                                    <div className="p-5 rounded-2xl bg-app-bg/60 border border-border-subtle shadow-subtle focus-within:border-primary/30 transition-all">
                                        <div className="flex flex-wrap gap-2 mb-4">
                                            {invitedMembers.length === 0 && (
                                                <p className="text-xs text-text-muted">No members added yet</p>
                                            )}
                                            {invitedMembers.map((m) => (
                                                <span
                                                    key={m._id || m.id}
                                                    className="inline-flex items-center gap-1.5 bg-primary/10 px-3 py-1.5 rounded-xl text-xs font-semibold text-primary flex items-center gap-2 border border-primary/20"
                                                >
                                                    {m.profilePhoto ? (
                                                        <img src={m.profilePhoto} alt="" className="w-4 h-4 rounded-full" />
                                                    ) : (
                                                        <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[8px] font-bold text-primary">
                                                            {(m.name || 'U').charAt(0)}
                                                        </div>
                                                    )}
                                                    {m.name || m.displayName}
                                                    <button
                                                        onClick={() =>
                                                            setInvitedMembers((prev) =>
                                                                prev.filter((x) => (x._id || x.id) !== (m._id || m.id))
                                                            )
                                                        }
                                                        className="hover:text-red-500 transition-colors ml-1"
                                                    >
                                                        <AppIcon name="XMarkIcon" size={12} />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                        <input
                                            value={inviteInput}
                                            disabled={isLaunching}
                                            onChange={(e) => setInviteInput(e.target.value)}
                                            className="w-full bg-transparent outline-none text-text-primary font-medium placeholder:text-text-muted/40 disabled:opacity-50"
                                            placeholder="Search for teammates..."
                                        />
                                    </div>
                                    
                                    <div className="space-y-2">
                                        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider block mb-3">Suggestions</span>
                                        {usersData
                                            .filter((s) => !invitedMembers.find(m => (m._id || m.id) === (s._id || s.id)) &&
                                                (s.name || s.username || '').toLowerCase().includes(inviteInput.toLowerCase()) &&
                                                (s._id || s.id) !== (user?._id || user?.id)
                                            )
                                            .slice(0, 5)
                                            .map((s) => (
                                                <button
                                                    key={s._id || s.id}
                                                    onClick={() => addMember(s)}
                                                    className="flex items-center gap-4 w-full text-left p-3.5 rounded-2xl transition-all group hover:bg-primary/5 border border-transparent hover:border-primary/10"
                                                >
                                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center text-sm font-bold text-primary border border-primary/20 group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all shadow-sm overflow-hidden">
                                                        {s.profilePhoto ? (
                                                            <img src={s.profilePhoto} className="w-full h-full object-cover" alt="" />
                                                        ) : (
                                                            (s.name || 'U').charAt(0).toUpperCase()
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-sm font-semibold text-text-primary group-hover:text-primary transition-colors block truncate">{s.name || s.username}</span>
                                                        {s.email && (
                                                            <span className="text-xs text-text-muted truncate block">{s.email}</span>
                                                        )}
                                                    </div>
                                                    <div className="opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0">
                                                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                                                            <AppIcon name="PlusIcon" size={16} className="text-white" />
                                                        </div>
                                                    </div>
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="flex justify-between items-center px-6 py-5 bg-app-bg/20 border-t border-border-subtle">
                            <button
                                onClick={step === 1 ? onClose : () => setStep(1)}
                                className="px-5 py-2.5 rounded-xl text-sm font-medium text-text-muted hover:text-text-primary hover:bg-black/5 transition-all flex items-center gap-2"
                            >
                                <AppIcon name={step === 1 ? "XMarkIcon" : "ArrowLeftIcon"} size={16} />
                                {step === 1 ? "Cancel" : "Back"}
                            </button>

                            <button
                                onClick={step === 1 ? handleCreate : handleLaunch}
                                disabled={(step === 1 && !name.trim()) || isLaunching}
                                className={`px-8 py-3 rounded-2xl text-sm font-semibold transition-all flex items-center gap-2 shadow-lg active:scale-95 ${
                                    (!name.trim() && step === 1) || isLaunching
                                        ? "bg-black/5 text-text-muted/30 cursor-not-allowed"
                                        : "bg-primary text-white shadow-primary/20 hover:bg-primary/90 hover:shadow-primary/30"
                                }`}
                            >
                                {isLaunching && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                {step === 1 ? "Continue" : "Create Channel"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}